import PageHeader from "@/components/page-header";
import {
  TitleAccountForm,
  titleAccountFormValues,
} from "@/components/title/title-account-form";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import {
  getTitleAccountOptions,
  getTitleAccountQueryKey,
  updateTitleAccountMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/title/account-setup")({
  component: TitleAccountSetupPage,
});

export function TitleAccountSetupPage() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const account = useQuery({
    ...getTitleAccountOptions(),
    enabled: isAuthSetupComplete,
  });
  const update = useMutation({ ...updateTitleAccountMutation() });

  if (!isAuthSetupComplete || account.isLoading) {
    return <LoadingScreen />;
  }
  if (account.error || !account.data) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(account.error)}
        message={getQueryErrorMessage(account.error)}
      />
    );
  }

  const isSecondary = Boolean(account.data.primary_id);
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <PageHeader
        title="Account details"
        subtitle="Tell us who should receive title responses."
        hideBackButton
      />
      <TitleAccountForm
        initial={titleAccountFormValues(account.data)}
        isSecondary={isSecondary}
        submitLabel="Save and continue"
        busy={update.isPending}
        onSubmit={async (values) => {
          try {
            const saved = await update.mutateAsync({
              body: {
                name: values.name,
                company_name: values.company_name,
                phone: values.phone,
                address1: values.address1,
                address2: values.address2,
                city: values.city,
                state: values.state,
                zipcode: values.zipcode,
              },
            });
            // The layout still has the incomplete account. Replace it before
            // leaving, or the setup check sends the user straight back here.
            queryClient.setQueryData(getTitleAccountQueryKey(), saved);
            toast({
              title: "Account saved",
              description: "Your title account is ready.",
            });
            await navigate({ to: "/title/requests" });
          } catch (error) {
            toast({
              title: "Could not save",
              description: getQueryErrorMessage(error),
              variant: "destructive",
            });
          }
        }}
      />
    </div>
  );
}
